import { useMemo } from "react";
import { UMBRALES } from "./useMapaCalorData";

// ─── Helpers ─────────────────────────────────────────────────────────────────
const getTier = (cat) => (cat === "A" || cat === "B" ? cat : "C");

const diasDesde = (fecha) => {
  if (!fecha) return Infinity;
  return Math.floor((Date.now() - new Date(fecha).getTime()) / (1000 * 60 * 60 * 24));
};

const colorFromDias = (dias, umbral) => {
  if (dias <= umbral.verde) return "verde";
  if (dias <= umbral.amarillo) return "amarillo";
  return "rojo";
};

const COLOR_PRIORIDAD = { rojo: 3, amarillo: 2, verde: 1 };

const peorColor = (colores) => {
  if (colores.length === 0) return "rojo";
  return colores.reduce((peor, c) => (COLOR_PRIORIDAD[c] > COLOR_PRIORIDAD[peor] ? c : peor), "verde");
};

const norm = (str) =>
  (str || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .trim();

// ─── Subcategorías (misma lógica que useMapaCalorData) ──────────────────────
const has = (empresa, kws) => {
  const u = norm(empresa);
  return kws.some((k) => u.includes(norm(k)));
};

const REGIONALES = ["JETSMART", "SKY", "ARAJET", "LATAM", "LAN", "COPA", "AEROLÍNEAS ARGENTINAS"];
const BANCOS_KW = [
  "BCI",
  "BTG",
  "BANCO FALABELLA",
  "CONSORCIO",
  "ITAÚ",
  "LA ARAUCANA",
  "SANTANDER",
  "SCOTIABANK",
  "BICE",
  "BANCO DE CHILE",
  "BANCO ESTADO",
  "BANCO RIPLEY",
];
const APPS_KW = ["UBER", "DIDI", "BEAT", "CABIFY", "RAPPI"];

const SUBCATEGORIA_RULES = {
  AEROLINEAS: (e) => (has(e, REGIONALES) ? "Regionales" : "Internacionales"),
  "BANCA & FINANZAS": (e) => {
    if (norm(e).startsWith("AFP")) return "AFP";
    return has(e, BANCOS_KW) ? "Bancos" : "Financieras";
  },
  MOVILIDAD: (e) => (has(e, APPS_KW) ? "Apps" : "Arriendo"),
};

const matchRule = (catNombre) => {
  const n = norm(catNombre);
  return Object.entries(SUBCATEGORIA_RULES).find(([key]) => norm(key) === n)?.[1];
};

const SUBCATEGORIA_ORDER = {
  AEROLINEAS: ["Regionales", "Internacionales"],
  "BANCA & FINANZAS": ["AFP", "Bancos", "Financieras"],
  AUTOMOTRIZ: ["Gama Alta", "Gama Media", "B2B", "Alquiler"],
};

const getSubOrder = (catNombre) => {
  const n = norm(catNombre);
  return Object.entries(SUBCATEGORIA_ORDER).find(([key]) => norm(key) === n)?.[1];
};

// Roles de contacto cliente (orden jerárquico)
export const ROLES_CLIENTE_ORDER = [
  "Gerente de Marketing",
  "Director de Marketing",
  "Jefe de Medios",
  "Jefe de Marca",
  "Brand Manager",
  "Supervisor",
  "Asistente",
];

export const ROLES_CLIENTE_SHORT = {
  "Gerente de Marketing": "Gerente Mkt",
  "Director de Marketing": "Director Mkt",
  "Jefe de Medios": "Jefe Medios",
  "Jefe de Marca": "Jefe Marca",
  "Brand Manager": "Brand Mgr",
  Supervisor: "Supervisor",
  Asistente: "Asistente",
};

const getRolIndex = (cargo) => {
  const idx = ROLES_CLIENTE_ORDER.indexOf(cargo);
  return idx !== -1 ? idx : 99;
};

// ─── Hook ────────────────────────────────────────────────────────────────────
export const useMapaCalorClientesData = (
  clientes,
  usuarios,
  categorias,
  actividades,
  contactosCliente = [],
  subcategoriasDB = []
) => {
  const usuariosMap = useMemo(() => {
    const map = {};
    usuarios.forEach((u) => {
      map[u.id] = u;
    });
    return map;
  }, [usuarios]);

  const categoriasMap = useMemo(() => {
    const map = {};
    categorias.forEach((c) => {
      map[c.id] = c;
    });
    return map;
  }, [categorias]);

  // Mapa: cliente_id → [{ usuario_id, cargo }] desde tabla relacional
  const contactosPorCliente = useMemo(() => {
    const map = {};
    contactosCliente.forEach((cc) => {
      if (!map[cc.cliente_id]) map[cc.cliente_id] = [];
      map[cc.cliente_id].push(cc);
    });
    return map;
  }, [contactosCliente]);

  const usaTablaRelacional = contactosCliente.length > 0;

  // Subcategorías: reglas por ID de categoría
  const subRulesById = useMemo(() => {
    const map = {};
    categorias.forEach((c) => {
      const rule = matchRule(c.nombre);
      if (rule) map[c.id] = rule;
    });
    return map;
  }, [categorias]);

  const subcategoriasDBMap = useMemo(() => {
    const map = {};
    subcategoriasDB.forEach((s) => {
      map[s.id] = s.nombre;
    });
    return map;
  }, [subcategoriasDB]);

  const data = useMemo(() => {
    if (clientes.length === 0 || usuarios.length === 0) return [];

    // 1. Enriquecer cada cliente con su equipo del lado cliente + calor
    const clientesEnriquecidos = clientes
      .map((cli) => {
        const tier = getTier(cli.categoria_cliente);
        const umbralCl = UMBRALES[tier].cliente;
        const actPerEmail = actividades[cli.id] || {};

        // Obtener contactos: preferir tabla relacional, fallback a campos legacy
        const ccRows = usaTablaRelacional && contactosPorCliente[cli.id] ? contactosPorCliente[cli.id] : [];
        const contactoIds =
          ccRows.length > 0
            ? ccRows.map((cc) => ({ id: cc.usuario_id, cargoOverride: cc.cargo }))
            : [
                cli.contacto_cliente && { id: cli.contacto_cliente, cargoOverride: null },
                cli.contacto_cliente_1 && { id: cli.contacto_cliente_1, cargoOverride: null },
                cli.contacto_cliente_2 && { id: cli.contacto_cliente_2, cargoOverride: null },
                cli.contacto_cliente_3 && { id: cli.contacto_cliente_3, cargoOverride: null },
              ].filter(Boolean);

        // Sin contactos NO excluye: el cliente igual se muestra (queda en rojo → pendiente)
        const contactos = contactoIds
          .map(({ id, cargoOverride }) => usuariosMap[id] && { user: usuariosMap[id], cargoOverride })
          .filter(Boolean)
          .map(({ user, cargoOverride }) => {
            const email = user.email?.toLowerCase();
            const lastDate = email ? actPerEmail[email] || null : null;
            const dias = diasDesde(lastDate);
            const cargo = cargoOverride || "—";
            return {
              ...user,
              dias,
              color: colorFromDias(dias, umbralCl),
              lastDate,
              cargo,
              rolIndex: getRolIndex(cargo),
              rolShort: ROLES_CLIENTE_SHORT[cargo] || cargo,
            };
          })
          .sort((a, b) => a.rolIndex - b.rolIndex);

        const colorCliente = peorColor(contactos.map((c) => c.color));

        // Separar: gerente (nivel 1), jefe (nivel 2), resto
        const gerente = contactos.find((c) => getRolIndex(c.cargo) <= 1) || null; // Gerente o Director Mkt
        const jefe = contactos.find((c) => getRolIndex(c.cargo) >= 2 && getRolIndex(c.cargo) <= 4) || null;
        const resto = contactos.filter((c) => c !== gerente && c !== jefe);

        return {
          ...cli,
          contactosEquipo: contactos,
          gerente,
          jefe,
          equipoResto: resto,
          colorCliente,
          categoriaNombre: categoriasMap[cli.categoria]?.nombre || "SIN CATEGORÍA",
          tier,
        };
      })
      .filter(Boolean);

    // 2. Asignar subcategoría a cada cliente
    clientesEnriquecidos.forEach((cli) => {
      if (cli.subcategoria_id && subcategoriasDBMap[cli.subcategoria_id]) {
        cli.subcategoria = subcategoriasDBMap[cli.subcategoria_id];
      } else {
        const rule = subRulesById[cli.categoria];
        cli.subcategoria = rule ? rule(cli.empresa) : "General";
      }
    });

    // 3. Agrupar por Categoría → Subcategoría → Empresa → Productos
    const porCategoria = {};

    clientesEnriquecidos.forEach((cli) => {
      const catId = cli.categoria || "sin";
      if (cli.categoriaNombre === "SIN CATEGORÍA") return;

      if (!porCategoria[catId]) {
        porCategoria[catId] = { id: catId, nombre: cli.categoriaNombre, subcategorias: {} };
      }

      const subKey = cli.subcategoria || "General";
      if (!porCategoria[catId].subcategorias[subKey]) {
        porCategoria[catId].subcategorias[subKey] = { nombre: subKey, empresas: {} };
      }

      const empresaKey = norm(cli.empresa || "SIN MARCA");
      if (!porCategoria[catId].subcategorias[subKey].empresas[empresaKey]) {
        porCategoria[catId].subcategorias[subKey].empresas[empresaKey] = {
          nombre: cli.empresa || "SIN MARCA",
          productos: [],
        };
      }
      porCategoria[catId].subcategorias[subKey].empresas[empresaKey].productos.push(cli);
    });

    // 4. Construir estructura final
    const PRIORIDAD = ["BANCA & FINANZAS", "EDUCACION", "GASTRONOMIA", "AUTOMOTRIZ", "TECNOLOGIA"].map(norm);

    const resultado = Object.values(porCategoria)
      .map((cat) => {
        const customOrder = getSubOrder(cat.nombre);

        const subcategorias = Object.values(cat.subcategorias)
          .map((sub) => {
            const empresas = Object.values(sub.empresas)
              .map((emp) => {
                const productos = emp.productos.sort((a, b) => (a.producto || "").localeCompare(b.producto || ""));
                return {
                  ...emp,
                  productos,
                  color: peorColor(productos.map((p) => p.colorCliente)),
                  totalProductos: productos.length,
                };
              })
              .sort((a, b) => a.nombre.localeCompare(b.nombre));

            return {
              ...sub,
              empresas,
              color: peorColor(empresas.map((e) => e.color)),
              totalClientes: empresas.reduce((s, e) => s + e.totalProductos, 0),
            };
          })
          .sort((a, b) => {
            if (customOrder) {
              const iA = customOrder.indexOf(a.nombre);
              const iB = customOrder.indexOf(b.nombre);
              if (iA !== -1 && iB !== -1) return iA - iB;
              if (iA !== -1) return -1;
              if (iB !== -1) return 1;
            }
            return a.nombre.localeCompare(b.nombre);
          });

        // Si solo hay una subcategoría "General", aplanar
        const soloGeneral = subcategorias.length === 1 && subcategorias[0].nombre === "General";

        return {
          ...cat,
          subcategorias,
          soloGeneral,
          color: peorColor(subcategorias.map((s) => s.color)),
          totalClientes: subcategorias.reduce((s, sub) => s + sub.totalClientes, 0),
        };
      })
      .sort((a, b) => {
        const iA = PRIORIDAD.indexOf(norm(a.nombre));
        const iB = PRIORIDAD.indexOf(norm(b.nombre));
        if (iA !== -1 && iB !== -1) return iA - iB;
        if (iA !== -1) return -1;
        if (iB !== -1) return 1;
        return a.nombre.localeCompare(b.nombre);
      });

    return resultado;
  }, [
    clientes,
    usuarios,
    actividades,
    categoriasMap,
    usuariosMap,
    contactosPorCliente,
    usaTablaRelacional,
    subRulesById,
    subcategoriasDBMap,
  ]);

  return { data };
};
